import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Layout,
  Model,
  Actions,
  DockLocation,
  I18nLabel,
} from "flexlayout-react";
import "flexlayout-react/style/combined.css";
import "./DockModeLayout.css";
import { useChartsStore } from "~/store/chartStore";
import { useSidebarFavoritesStore } from "~/store/sidebarFavoritesStore";
import useWorkspaceStore from "~/store/workspaceStore";
import useBookByPriceStore from "~/store/bookByPriceStore";
import useWatchlistStore from "~/store/watchlistStore";
import { useThemeStore } from "~/store/themeStore";
import { isDarkTheme } from "~/config/themes";
import { useSettingsStore } from "~/store/settingsStore";
import { isFeatureEnabled } from "~/data/Services/settings";
import ChartPanel from "./ChartPanel";
import PositionsPanel from "./PositionsPanel";
import OpenOrdersPanel from "./OpenOrdersPanel";
import LogPanel from "./LogPanel";
import InfoPanel from "./InfoPanel";
import WatchlistPanel from "./WatchlistPanel";
import BookByPricePanel from "./BookByPricePanel";
import { GroupBadge, GroupSelector, GROUP_COLORS } from "./GroupSelector";
import PopoutPanelBridge from "./PopoutPanelBridge";

// A flexlayout só traz rótulos em inglês (tooltips dos botões da barra de abas).
// Devolver undefined mantém o texto padrão da lib.
const FLEXLAYOUT_LABELS_PT = {
  [I18nLabel.Close_Tab]: "Fechar aba",
  [I18nLabel.Close_Tabset]: "Fechar painel",
  [I18nLabel.Active_Tabset]: "Painel ativo",
  [I18nLabel.Move_Tabset]: "Mover painel",
  [I18nLabel.Move_Tabs]: "Mover abas",
  [I18nLabel.Maximize]: "Maximizar painel",
  [I18nLabel.Restore]: "Restaurar painel",
  [I18nLabel.Popout_Tab]: "Destacar aba em nova janela",
  [I18nLabel.Popout_Tab_Float]: "Flutuar aba",
  [I18nLabel.Overflow_Menu_Tooltip]: "Abas ocultas",
  [I18nLabel.Error_rendering_component]: "Erro ao renderizar o componente",
  [I18nLabel.Error_rendering_component_retry]: "Tentar novamente",
};

const flexlayoutI18n = (id) => FLEXLAYOUT_LABELS_PT[id];

// Rótulo do botão que substitui o "destacar" dentro de uma janela destacada.
const RESTORE_TAB_TITLE = "Restaurar aba na janela principal";

// Espelho do ícone nativo de destacar (quadrado + seta): mesma caixa, mas a seta
// gira 180° em torno do próprio centro e passa a APONTAR PARA DENTRO da caixa.
const RESTORE_ICON_STYLE = {
  width: "1em",
  height: "1em",
  display: "flex",
  alignItems: "center",
};

const RestoreTabIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    style={RESTORE_ICON_STYLE}
    viewBox="0 0 20 20"
    fill="var(--color-icon)"
  >
    <g transform="rotate(180 12.15 7.85)">
      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
    </g>
    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
  </svg>
);

// Panel-type icon shown at the start of every FlexLayout tab header — the exact
// icon class from the matching navbar button (new-chart button + dock-panel
// buttons) so users recognise each panel type at a glance. chart-icon-candle is
// an SVG; the rest are codicon font glyphs — store the full className for each.
const PANEL_ICONS = {
  chart: "chart-icon-candle",
  grid: "codicon codicon-eye",
  bookByPrice: "codicon codicon-list-ordered",
  positions: "codicon codicon-briefcase",
  openOrders: "codicon codicon-list-selection",
  log: "codicon codicon-output",
  info: "codicon codicon-info",
};

// Wrapper component for GroupBadge in tab header with its own ref
function TabGroupBadge({ chartId, group, onBadgeClick }) {
  const ref = useRef(null);

  const handleClick = (e) => {
    onBadgeClick(chartId, ref, e);
  };

  return (
    <span ref={ref} style={{ display: "inline-flex", alignItems: "center" }}>
      <GroupBadge
        group={group}
        onClick={handleClick}
        data-evo-id="dockmodelayout-badge-group"
      />
    </span>
  );
}

function TabScrollArrow({ direction }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const outer = el.closest(".flexlayout__tabset_tabbar_outer");
    const container = outer?.querySelector(".flexlayout__tabset_tabbar_inner");
    if (!container) return;

    const update = () => {
      const hasOverflow = container.scrollWidth > container.clientWidth + 1;
      if (!hasOverflow) {
        setVisible(false);
        return;
      }
      if (direction === "left") {
        setVisible(container.scrollLeft > 1);
      } else {
        setVisible(
          container.scrollLeft <
            container.scrollWidth - container.clientWidth - 1,
        );
      }
    };

    update();
    container.addEventListener("scroll", update);
    const ro = new ResizeObserver(update);
    ro.observe(container);
    // Also observe children changes (tabs added/removed)
    const mo = new MutationObserver(update);
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      container.removeEventListener("scroll", update);
      ro.disconnect();
      mo.disconnect();
    };
  }, [direction]);

  const handleClick = (e) => {
    e.stopPropagation();
    const outer = ref.current?.closest(".flexlayout__tabset_tabbar_outer");
    const container = outer?.querySelector(".flexlayout__tabset_tabbar_inner");
    if (!container) return;
    container.scrollBy({
      left: direction === "left" ? -120 : 120,
      behavior: "smooth",
    });
  };

  return (
    <button
      ref={ref}
      className={`tab-scroll-arrow tab-scroll-arrow--${direction}`}
      style={{ visibility: visible ? "visible" : "hidden" }}
      onClick={handleClick}
      onDoubleClick={(e) => e.stopPropagation()}
      title={
        direction === "left"
          ? "Rolar abas para a esquerda"
          : "Rolar abas para a direita"
      }
      data-evo-id="dockmodelayout-btn-scroll-tabs"
    >
      {direction === "left" ? "\u25C0" : "\u25B6"}
    </button>
  );
}

// Panel types eligible for window popout when settings.features.panelPopout
// is on (release flag — flip via runtime-config.json, no rebuild). Per-tab
// enablePopout overrides the global tabEnableFloat:false.
const POPOUT_PANELS = new Set([
  "grid",
  "chart",
  "bookByPrice",
  "positions",
  "openOrders",
  "log",
  "info",
]);

// #popout: the active-tabset border is the order/indicator target indicator —
// it must NEVER light up in two windows at once. FlexLayout 0.9 keeps one
// active tabset PER window (layout), so whenever one window gains the active
// tabset, clear every other window's. Loop-safe: the clearing action carries
// tabsetNode undefined, which the onModelChange dispatcher ignores.
function enforceSingleActiveTabset(model, keepLayoutId) {
  const layouts = model.getLayouts?.();
  if (!layouts || layouts.size <= 1) return;
  for (const layoutId of layouts.keys()) {
    if (layoutId === keepLayoutId) continue;
    if (model.getActiveTabset(layoutId)) {
      model.doAction(Actions.setActiveTabset(undefined, layoutId));
    }
  }
}

// Which layout (window) gained focus in this action — null when the action
// doesn't represent a focus gain (or is our own clearing action).
function focusGainLayoutId(model, action) {
  if (!action) return null;
  if (action.type === Actions.SET_ACTIVE_TABSET) {
    if (!action.data?.tabsetNode) return null; // clearing — don't cascade
    return action.data.layoutId ?? Model.MAIN_LAYOUT_ID;
  }
  // Native activations: selecting a tab, dropping a moved tabset and popping
  // a tab out all activate the target window's tabset inside the lib.
  let nodeId = null;
  if (action.type === Actions.SELECT_TAB) nodeId = action.data?.tabNode;
  else if (
    action.type === Actions.POPOUT_TAB ||
    action.type === Actions.POPOUT_TABSET
  )
    nodeId = action.data?.node;
  else if (action.type === Actions.MOVE_NODE) nodeId = action.data?.fromNode;
  if (!nodeId) return null;
  const node = model.getNodeById?.(nodeId);
  return node?.getLayoutId?.() ?? null;
}

// Onde a aba estava ancorada antes de ser destacada — guardado no config da aba, que
// é serializado no JSON do desktop, então sobrevive a save/load. Os ids de tabset são
// regenerados a cada fromJson (rebuild/troca de desktop), por isso o hint também leva
// âncoras estáveis: os ids das abas irmãs e os chartIds delas.
function captureHomeHint(model, tabNode) {
  if (!model || !tabNode) return null;
  if (tabNode.getLayoutId?.() !== Model.MAIN_LAYOUT_ID) return null;
  const tabset = tabNode.getParent?.();
  if (!tabset || tabset.getType?.() !== "tabset") return null;

  const siblings = tabset.getChildren().filter((c) => c !== tabNode);
  const hint = {
    tabsetId: tabset.getId(),
    index: tabset.getChildren().indexOf(tabNode),
    siblingTabIds: siblings.map((c) => c.getId()),
    siblingChartIds: siblings
      .map((c) => c.getConfig?.()?.chartId)
      .filter(Boolean),
  };

  // Aba sozinha no tabset (uma célula da grade): ao destacar, a lib remove o tabset
  // vazio — não há para onde voltar. Guarda a posição da célula em relação a um tabset
  // vizinho (âncora + lado), para recriar a célula no mesmo lugar na restauração.
  if (siblings.length === 0) {
    const parentRow = tabset.getParent?.();
    const rowChildren = parentRow?.getChildren?.() || [];
    const idx = rowChildren.indexOf(tabset);
    const neighbor = rowChildren[idx - 1] || rowChildren[idx + 1] || null;

    if (neighbor) {
      // Primeira aba dentro do vizinho (pode ser um tabset ou uma sub-row).
      let anchorTab = null;
      const findTab = (node) => {
        if (anchorTab) return;
        if (node.getType?.() === "tab") {
          anchorTab = node;
          return;
        }
        (node.getChildren?.() || []).forEach(findTab);
      };
      findTab(neighbor);

      // Lado deduzido da geometria (a lib não expõe a orientação da row).
      const a = tabset.getRect?.();
      const b = neighbor.getRect?.();
      let side = null;
      if (a && b) {
        const horizontal = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);
        if (horizontal) side = a.x >= b.x ? "right" : "left";
        else side = a.y >= b.y ? "bottom" : "top";
      }

      if (anchorTab && side) {
        hint.cell = {
          anchorTabId: anchorTab.getId(),
          anchorChartId: anchorTab.getConfig?.()?.chartId ?? null,
          side,
        };
      }
    }
  }

  return hint;
}

// Tabset da janela principal onde a aba deve voltar, segundo o hint (id direto ou, se o
// tabset foi recriado, o tabset que hoje contém alguma das abas irmãs).
function findHomeTabset(model, hint) {
  if (!hint) return null;
  const byId = hint.tabsetId ? model.getNodeById(hint.tabsetId) : null;
  if (
    byId?.getType?.() === "tabset" &&
    byId.getLayoutId?.() === Model.MAIN_LAYOUT_ID
  ) {
    return byId;
  }

  const siblingTabIds = new Set(hint.siblingTabIds || []);
  const siblingChartIds = new Set(hint.siblingChartIds || []);
  let match = null;
  const walk = (node) => {
    if (match) return;
    if (node.getType?.() === "tabset") {
      const hit = node
        .getChildren()
        .some(
          (tab) =>
            siblingTabIds.has(tab.getId()) ||
            siblingChartIds.has(tab.getConfig?.()?.chartId),
        );
      if (hit) {
        match = node;
        return;
      }
    }
    (node.getChildren?.() || []).forEach(walk);
  };
  walk(model.getRootRow()); // só a janela principal
  return match;
}

// Tabset vizinho (na principal) da célula onde a aba estava, quando ela era a única do
// seu tabset e ele deixou de existir ao ser destacada. Devolve { tabsetId, side }.
function findHomeCell(model, hint) {
  const cell = hint?.cell;
  if (!cell) return null;

  let anchorTab = null;
  const walk = (node) => {
    if (anchorTab) return;
    if (node.getType?.() === "tab") {
      if (
        node.getId() === cell.anchorTabId ||
        (cell.anchorChartId &&
          node.getConfig?.()?.chartId === cell.anchorChartId)
      ) {
        anchorTab = node;
      }
      return;
    }
    (node.getChildren?.() || []).forEach(walk);
  };
  walk(model.getRootRow()); // só a janela principal

  const tabset = anchorTab?.getParent?.();
  if (!tabset || tabset.getType?.() !== "tabset") return null;

  const locations = {
    right: DockLocation.RIGHT,
    left: DockLocation.LEFT,
    bottom: DockLocation.BOTTOM,
    top: DockLocation.TOP,
  };
  const location = locations[cell.side];
  if (!location) return null;

  return { tabsetId: tabset.getId(), location };
}

// Sweep: chart tabs are created at several sites (makeTab, addNode calls) and
// persisted desktops predate the flag, so instead of touching every creation
// site the flag is applied per-tab post-hoc. BIDIRECTIONAL and self-healing:
// enablePopout is forced to match the feature flag, so desktops saved while
// the flag was on lose the popout button when it's turned off (same class of
// problem as the persisted tabEnableFloat sanitization). Idempotent — the
// isEnablePopout guard prevents doAction→onModelChange loops.
function applyPopoutFeatureFlag(model) {
  if (!model) return;
  const enabled = isFeatureEnabled("panelPopout");
  const pending = [];
  model.visitNodes((node) => {
    if (node.getType() !== "tab" || !POPOUT_PANELS.has(node.getComponent?.())) {
      return;
    }
    // O ícone nativo de destacar só faz sentido na janela principal: dentro de uma
    // janela destacada ele é substituído pelo nosso botão "Restaurar aba" (a aba
    // continua com enablePopout, o que permite movê-la entre janelas).
    const inMainWindow = node.getLayoutId?.() === Model.MAIN_LAYOUT_ID;
    const wantIcon = enabled && inMainWindow;
    const attrs = {};
    if (node.isEnablePopout?.() !== enabled) attrs.enablePopout = enabled;
    if (node.isEnablePopoutIcon?.() !== wantIcon)
      attrs.enablePopoutIcon = wantIcon;
    if (Object.keys(attrs).length > 0) pending.push([node.getId(), attrs]);
  });
  pending.forEach(([id, attrs]) =>
    model.doAction(Actions.updateNodeAttributes(id, attrs)),
  );
}

function createDefaultDockModel(charts, legacyPanelHints = null) {
  const globalConfig = {
    tabEnableFloat: false,
    tabEnableClose: true,
    tabEnableRename: false,
    tabSetMinWidth: 80,
    tabSetMinHeight: 80,
    // splitterSize/splitterExtra were removed as global attrs in flexlayout 0.9 —
    // sizing now lives in CSS vars (--splitter-size/--splitter-active-size) in DockModeLayout.css
  };

  // Legacy desktops used tabs — put all charts in one tabset
  // New desktops use smart grid layout
  const makeTab = (chart) => ({
    type: "tab",
    name: `${chart.symbol || "New"} ${chart.periodicity || ""}`.trim(),
    component: "chart",
    config: { chartId: chart.id },
  });
  const chartChildren = legacyPanelHints?.tabMode
    ? [{ type: "tabset", weight: 50, children: charts.map(makeTab) }]
    : buildSmartGridChildren(charts);

  // If no legacy hints or no panels were open, use charts-only layout
  const hasRouterInfo = legacyPanelHints?.routerInfoPanel;
  const hasLog = legacyPanelHints?.logPanel;

  if (!hasRouterInfo && !hasLog) {
    return Model.fromJson({
      global: globalConfig,
      layout: { type: "row", weight: 100, children: chartChildren },
    });
  }

  // Build layout with legacy panels
  let rootChildren;

  // Wrap chart children in a single container so weights work correctly alongside panels
  const chartArea =
    chartChildren.length === 1
      ? { ...chartChildren[0], weight: 75 }
      : { type: "row", weight: 75, children: chartChildren };

  if (hasRouterInfo && hasLog) {
    // Left sidebar (Positions + Open Orders) + Right column (Charts + Log at bottom)
    const leftCol = {
      type: "row",
      weight: 25,
      children: [
        {
          type: "tabset",
          children: [{ type: "tab", name: "Posições", component: "positions" }],
        },
        {
          type: "tabset",
          children: [
            { type: "tab", name: "Ordens Abertas", component: "openOrders" },
          ],
        },
      ],
    };
    const rightCol = {
      type: "row",
      weight: 75,
      children: [
        { ...chartArea, weight: 75 },
        {
          type: "tabset",
          weight: 25,
          children: [{ type: "tab", name: "Log", component: "log" }],
        },
      ],
    };
    rootChildren = [leftCol, rightCol];
  } else if (hasRouterInfo) {
    // Left sidebar only
    const leftCol = {
      type: "row",
      weight: 25,
      children: [
        {
          type: "tabset",
          children: [{ type: "tab", name: "Posições", component: "positions" }],
        },
        {
          type: "tabset",
          children: [
            { type: "tab", name: "Ordens Abertas", component: "openOrders" },
          ],
        },
      ],
    };
    rootChildren = [leftCol, chartArea];
  } else {
    // Log at bottom only — nested row flips to vertical so log sits below charts
    rootChildren = [
      {
        type: "row",
        weight: 100,
        children: [
          { ...chartArea, weight: 75 },
          {
            type: "tabset",
            weight: 25,
            children: [{ type: "tab", name: "Log", component: "log" }],
          },
        ],
      },
    ];
  }

  console.log("[DockMode] Created layout with legacy panels:", {
    routerInfoPanel: hasRouterInfo,
    logPanel: hasLog,
  });

  return Model.fromJson({
    global: globalConfig,
    layout: { type: "row", weight: 100, children: rootChildren },
  });
}

function getChartIdsFromModel(model) {
  const ids = new Set();
  model.visitNodes((node) => {
    if (node.getType() === "tab" && node.getComponent() === "chart") {
      const chartId = node.getConfig()?.chartId;
      if (chartId) ids.add(chartId);
    }
  });
  return ids;
}

// Build smart grid layout children JSON for N charts
// 1: single | 2: side-by-side | 3-4: 2×2 | 5-6: 3×2
function buildSmartGridChildren(charts) {
  const makeTab = (chart) => ({
    type: "tab",
    name: `${chart.symbol || "New"} ${chart.periodicity || ""}`.trim(),
    component: "chart",
    config: { chartId: chart.id },
  });
  const makeTabset = (chart) => ({
    type: "tabset",
    weight: 50,
    children: [makeTab(chart)],
  });
  const n = charts.length;

  if (n === 0) return [{ type: "tabset", weight: 50, children: [] }];
  if (n === 1) return [makeTabset(charts[0])];
  if (n <= 2) return charts.map(makeTabset);

  if (n <= 4) {
    // 2 columns: col1=[0,2], col2=[1,3]  →  [0][1] / [2][3]
    const col1 = charts.filter((_, i) => i % 2 === 0).map(makeTabset);
    const col2 = charts.filter((_, i) => i % 2 === 1).map(makeTabset);
    return [
      { type: "row", weight: 50, children: col1 },
      { type: "row", weight: 50, children: col2 },
    ];
  }

  // 5+ charts: compute grid dimensions and distribute round-robin across columns
  const numCols = Math.ceil(Math.sqrt(n));
  const cols = Array.from({ length: numCols }, (_, c) =>
    charts.filter((_, i) => i % numCols === c).map(makeTabset),
  ).filter((c) => c.length > 0);
  return cols.map((col) => ({
    type: "row",
    weight: 100 / cols.length,
    children: col,
  }));
}

// Collect all non-chart tab definitions from the entire model tree.
// Returns tab definitions for panels like positions, openOrders, log, grid.
// Chart ids docked in a layout JSON tree (the main layout, NOT the popout windows —
// those live in json.subLayouts). Organizar só reorganiza o que está na principal.
function collectDockedChartIds(layoutJson) {
  const ids = new Set();
  const walk = (node) => {
    if (!node) return;
    if (node.type === "tab" && node.component === "chart") {
      const chartId = node.config?.chartId;
      if (chartId) ids.add(chartId);
    }
    (node.children || []).forEach(walk);
  };
  walk(layoutJson);
  return ids;
}

function collectNonChartTabs(layoutJson) {
  const tabs = [];
  const walk = (node) => {
    if (!node) return;
    if (node.type === "tab" && node.component && node.component !== "chart") {
      tabs.push({
        type: "tab",
        // Preserve the original tab id so per-node state (BookByPricePanel
        // instanceId, etc.) survives Organize-as-Tabs / Organize-as-Grid
        // round-trips. Dropping it makes FlexLayout assign a fresh id on
        // rebuild and orphans the panel's store entry (#334).
        ...(node.id ? { id: node.id } : {}),
        name: node.name,
        component: node.component,
        ...(node.config ? { config: node.config } : {}),
      });
    }
    (node.children || []).forEach(walk);
  };
  walk(layoutJson);
  return tabs;
}

function countTabsets(model) {
  let count = 0;
  model.visitNodes((node) => {
    if (node.getType() === "tabset") count++;
  });
  return count;
}

// Find the dedicated non-chart panel column in the model (left column with only non-chart tabsets).
function findPanelColumnNode(model) {
  const root = model.getRootRow();
  for (const child of root.getChildren()) {
    if (child.getType() === "row") {
      const children = child.getChildren();
      if (children.length === 0) continue;
      const allNonChart = children.every((c) => {
        if (c.getType() !== "tabset") return false;
        return c.getChildren().every((t) => t.getComponent?.() !== "chart");
      });
      if (allNonChart) return child;
    }
    if (child.getType() === "tabset") {
      const allNonChart = child
        .getChildren()
        .every((t) => t.getComponent?.() !== "chart");
      if (allNonChart && child.getChildren().length > 0) return child;
    }
  }
  return null;
}

function nodeHasChartTabs(node) {
  if (!node) return false;
  if (node.getType() === "tabset") {
    return node.getChildren().some((tab) => tab.getComponent?.() === "chart");
  }
  return node.getChildren().some(nodeHasChartTabs);
}

// Returns the node that acts as the chart grid container.
// It descends through mixed layout branches until it reaches the subtree whose
// direct children are all chart columns/tabsets.
function findChartContainerNode(model) {
  const descend = (node) => {
    if (!node || node.getType() === "tabset") return node;

    const children = node.getChildren();
    const chartChildren = children.filter(nodeHasChartTabs);

    if (chartChildren.length === 0) return node;
    if (chartChildren.length === children.length) return node;
    if (chartChildren.length === 1) return descend(chartChildren[0]);

    return node;
  };

  return descend(model.getRootRow());
}

function getGridColumnHeight(node) {
  if (!node) return 0;
  return node.getType() === "tabset" ? 1 : node.getChildren().length;
}

function addChartNodeToGrid(model, chart, totalCharts) {
  const chartContainer = findChartContainerNode(model);
  const rawContainerChildren =
    chartContainer.getType() === "tabset"
      ? [chartContainer]
      : chartContainer.getChildren();
  // Skip panel columns (Watchlist/Posições/Ordens/Log) so new charts never land
  // inside them when the layout is "flat" (chart tabsets share root row with a
  // panelCol). See issue #276.
  const containerChildren = rawContainerChildren.filter(nodeHasChartTabs);

  const targetCols = Math.ceil(Math.sqrt(totalCharts));
  const tabNode = {
    type: "tab",
    name: `${chart.symbol || "New"} ${chart.periodicity || ""}`.trim(),
    component: "chart",
    config: { chartId: chart.id },
  };

  if (containerChildren.length < targetCols) {
    model.doAction(
      Actions.addNode(tabNode, chartContainer.getId(), DockLocation.RIGHT, -1),
    );
    return;
  }

  let shortestColumn = containerChildren[containerChildren.length - 1];
  let minCount = Infinity;

  containerChildren.forEach((child) => {
    const count = getGridColumnHeight(child);
    if (count < minCount) {
      minCount = count;
      shortestColumn = child;
    }
  });

  let targetNode = shortestColumn;
  if (shortestColumn.getType() !== "tabset") {
    const children = shortestColumn.getChildren();
    targetNode = children[children.length - 1];
  }

  model.doAction(
    Actions.addNode(tabNode, targetNode.getId(), DockLocation.BOTTOM, -1),
  );
}

function rebalanceGridNode(model, node) {
  if (!node || node.getType() === "tabset") return;

  const children = node.getChildren();
  if (children.length > 1) {
    model.doAction(
      Actions.adjustWeights(
        node.getId(),
        children.map(() => 100 / children.length),
      ),
    );
  }

  children.forEach((child) => {
    if (child.getType() !== "tabset") {
      rebalanceGridNode(model, child);
    }
  });
}

function rebalanceChartGrid(model) {
  const chartContainer = findChartContainerNode(model);
  rebalanceGridNode(model, chartContainer);
}

function getBookByPriceTabName(symbol, viewMode) {
  if (!symbol) return "Livro de Ofertas";
  return `${symbol} ${viewMode === "superdom" ? "DoM" : "Livro"}`;
}

// Reconcile chart + book-panel tab names from live state. Used by the reactive
// rename effect AND right after a Model.fromJson rebuild (Organizar em
// Abas/Grade), where that effect won't fire because its deps are unchanged on a
// pure layout toggle — so the book tab would otherwise keep the stale persisted
// name (e.g. generic "Livro de Ofertas"). (#334)
// Seed BookByPrice store instances from a (re)built model's saved node configs.
// INACTIVE book tabs never mount on desktop load, so BookByPricePanel.initInstance
// never runs for them and they have no entry in bookByPriceStore.instances — which
// means groupLinkStore.propagateSymbol can't reach them (they never follow the
// group) and reconcileTabNames can't name them, until the tab is clicked. Seeding
// from node.config.bookByPriceState makes them exist immediately. initInstance is a
// no-op when the instance already exists, so this is safe on every rebuild. (#334)
function seedBookInstancesFromModel(model) {
  if (!model) return;
  const { initInstance } = useBookByPriceStore.getState();
  model.visitNodes((node) => {
    if (node.getType() === "tab" && node.getComponent() === "bookByPrice") {
      initInstance(node.getId(), node.getConfig()?.bookByPriceState || {});
    }
  });
}

function reconcileTabNames(model, charts) {
  if (!model) return;
  model.visitNodes((node) => {
    if (node.getType() !== "tab") return;
    const component = node.getComponent();
    if (component === "chart") {
      const chartId = node.getConfig()?.chartId;
      const chart = charts.find((c) => c.id === chartId);
      if (!chart) return;
      const newName =
        `${chart.symbol || "New"} ${chart.periodicity || ""}`.trim();
      if (node.getName() !== newName) {
        model.doAction(Actions.renameTab(node.getId(), newName));
      }
    } else if (component === "bookByPrice") {
      const nodeId = node.getId();
      const inst = useBookByPriceStore.getState().instances[nodeId];
      const newName = getBookByPriceTabName(inst?.symbol, inst?.viewMode);
      if (node.getName() !== newName) {
        model.doAction(Actions.renameTab(nodeId, newName));
      }
    }
  });
}

function getPanelTypesFromModel(model) {
  const panels = {
    positions: false,
    openOrders: false,
    log: false,
    grid: false,
    info: false,
  };
  model.visitNodes((node) => {
    if (node.getType() === "tab") {
      const comp = node.getComponent();
      if (comp === "positions") panels.positions = true;
      if (comp === "openOrders") panels.openOrders = true;
      if (comp === "log") panels.log = true;
      if (comp === "grid") panels.grid = true;
      if (comp === "info") panels.info = true;
    }
  });
  return panels;
}

const DockModeLayout = ({ charts, onInteractiveApiReady }) => {
  const dockLayout = useWorkspaceStore((state) => state.dockLayout);
  const setDockLayout = useWorkspaceStore((state) => state.setDockLayout);
  const dockLayoutMode = useWorkspaceStore((state) => state.dockLayoutMode);
  const setDockLayoutMode = useWorkspaceStore(
    (state) => state.setDockLayoutMode,
  );
  const setDockPanelOpen = useWorkspaceStore((state) => state.setDockPanelOpen);
  const setPendingInfoInject = useWorkspaceStore(
    (state) => state.setPendingInfoInject,
  );
  const setAddDockPanelFn = useWorkspaceStore(
    (state) => state.setAddDockPanelFn,
  );
  const chartAddMode = useWorkspaceStore((state) => state.chartAddMode);
  const isRouterOperationsSidebarOpen = useWorkspaceStore(
    (state) => state.isRouterOperationsSidebarOpen,
  );
  const sidebarOpen = useSidebarFavoritesStore((state) => state.sidebarOpen);
  const setFocusedChart = useChartsStore((state) => state.setFocusedChart);
  const setFocusedPanelType = useWorkspaceStore(
    (state) => state.setFocusedPanelType,
  );
  const removeChart = useChartsStore((state) => state.removeChart);
  const updateChartGroup = useChartsStore((state) => state.updateChartGroup);
  // Display symbol: mirrors the last focused BBP instance (triggers tab renaming)
  const bbpDisplaySymbol = useBookByPriceStore((state) => state.symbol);
  // Per-instance state map (group badges, tab names)
  const bbpInstances = useBookByPriceStore((state) => state.instances);
  const watchlistGroup = useWatchlistStore((state) => state.group);
  const theme = useThemeStore((state) => state.theme);
  const defaultPanelToolbarPinned = useSettingsStore(
    (state) => state.defaultPanelToolbarPinned ?? true,
  );
  const isDark = isDarkTheme(theme);
  const isSyncingRef = useRef(false);
  // Origem da aba que está sendo destacada (ver captureHomeHint) — #popout.
  const pendingHomeHintRef = useRef(null);
  const modelRef = useRef(null);
  const layoutComponentRef = useRef(null);
  // Stable per-node focus-claim callbacks (memoized panels must not get unstable props)
  const claimFocusCacheRef = useRef(new Map());
  const [tabsetCount, setTabsetCount] = useState(0);
  const lastFocusedPanelTypeRef = useRef(null);
  const lastFocusedChartIdRef = useRef(null);
  const [groupSelectorState, setGroupSelectorState] = useState({
    open: false,
    chartId: null,
    anchorRef: null,
  });
  const layoutInvalidationKey = `objects:${sidebarOpen ? 1 : 0}|router:${isRouterOperationsSidebarOpen ? 1 : 0}`;

  // Sync focus state from FlexLayout model to Zustand store.
  // Called from onModelChange (universal sync point) and after model rebuilds.
  // Uses refs to avoid unnecessary store updates on drag/resize events.
  const syncFocusFromModel = (model) => {
    // FlexLayout 0.9 keeps one active tabset PER window (layout) — search every
    // layout so focusing a panel in a popout window really moves the app focus
    // (order/indicator/spotlight target), not just the border. After
    // enforceSingleActiveTabset at most one layout has an active tabset.
    let activeTabset = null;
    const layouts = model.getLayouts?.();
    if (layouts) {
      for (const layoutId of layouts.keys()) {
        activeTabset = model.getActiveTabset(layoutId);
        if (activeTabset) break;
      }
    } else {
      activeTabset = model.getActiveTabset();
    }
    let selectedTab = activeTabset?.getSelectedNode?.();

    // Fresh models (after Model.fromJson — desktop switch, Organizar em Abas, etc.)
    // have no active tabset until the user clicks. Fall back to the first chart tab
    // so the store's focusedChartId matches the visually selected tab; otherwise
    // navbar commands (periodicity, indicators) route to a stale chart. (#308)
    if (!selectedTab) {
      model.visitNodes((node) => {
        if (selectedTab) return;
        if (node.getType() === "tab" && node.getComponent() === "chart") {
          selectedTab = node;
        }
      });
    }

    // Fresh models (e.g. after desktop switch) may have no active tabset yet.
    // Default to 'chart' so stale focus from the previous desktop doesn't persist.
    const component = selectedTab?.getComponent?.() || "chart";
    if (component !== lastFocusedPanelTypeRef.current) {
      lastFocusedPanelTypeRef.current = component;
      setFocusedPanelType(component);
    }

    if (component === "chart") {
      const chartId = selectedTab?.getConfig?.()?.chartId;
      if (chartId && chartId !== lastFocusedChartIdRef.current) {
        lastFocusedChartIdRef.current = chartId;
        setFocusedChart(chartId);
      }
    }
  };

  // Create or restore model on first render
  if (!modelRef.current) {
    if (dockLayout) {
      try {
        const json = {
          ...dockLayout,
          global: {
            ...dockLayout.global,
            tabSetMinWidth: 80,
            tabSetMinHeight: 80,
            tabEnableRename: false,
          },
        };
        modelRef.current = Model.fromJson(json);
        // Guard: if persisted layout has no chart tabs but store has charts, the layout is corrupted.
        // Fall back to default layout to break the empty-layout cascade.
        const modelChartIds = getChartIdsFromModel(modelRef.current);
        if (modelChartIds.size === 0 && charts.length > 0) {
          console.warn(
            "[DockMode] Layout has 0 chart tabs but",
            charts.length,
            "charts in store — rebuilding default layout",
          );
          modelRef.current = createDefaultDockModel(charts);
        }
      } catch (e) {
        console.warn(
          "[DockMode] Failed to restore layout, creating default:",
          e,
        );
        modelRef.current = createDefaultDockModel(charts);
      }
    } else {
      // Check for legacy panel hints from old desktops (pre-dock mode)
      const hints = useWorkspaceStore.getState()._legacyPanelHints;
      modelRef.current = createDefaultDockModel(charts, hints);
      // Clear hints after use (one-time migration)
      if (hints) {
        useWorkspaceStore.setState({ _legacyPanelHints: null });
      }
    }
    // Sync dockPanels state from restored model (must set false too, in case
    // previous store state had a panel marked open that isn't in this layout)
    const panels = getPanelTypesFromModel(modelRef.current);
    Object.entries(panels).forEach(([type, isOpen]) => {
      setDockPanelOpen(type, isOpen);
    });

    // Sync tabset count for active-border visibility
    setTabsetCount(countTabsets(modelRef.current));

    // Sync initial focus from the active tabset's selected tab
    syncFocusFromModel(modelRef.current);
  }

  const updatePanelToolbarPinned = useCallback(
    (nodeId, pinned) => {
      const model = modelRef.current;
      const node = model?.getNodeById?.(nodeId);
      if (!model || !node) return;

      const config = node.getConfig?.() || {};
      if (config.panelToolbar?.pinned === pinned) return;

      model.doAction(
        Actions.updateNodeAttributes(nodeId, {
          config: {
            ...config,
            panelToolbar: {
              ...(config.panelToolbar || {}),
              pinned,
            },
          },
        }),
      );
      setDockLayout(model.toJson());
    },
    [setDockLayout],
  );

  const getPanelToolbarProps = useCallback(
    (node) => {
      const config = node.getConfig?.() || {};
      return {
        initialToolbarPinned:
          config.panelToolbar?.pinned ?? defaultPanelToolbarPinned,
        onToolbarPinnedChange: (pinned) =>
          updatePanelToolbarPinned(node.getId(), pinned),
      };
    },
    [defaultPanelToolbarPinned, updatePanelToolbarPinned],
  );

  const updateBookByPriceNodeState = useCallback(
    (nodeId, partialState) => {
      const model = modelRef.current;
      const node = model?.getNodeById?.(nodeId);
      if (!model || !node) return;

      const config = node.getConfig?.() || {};
      const currentState = config.bookByPriceState || {};
      const nextState = {
        ...currentState,
        ...partialState,
      };

      if (
        currentState.symbol === nextState.symbol &&
        currentState.group === nextState.group &&
        currentState.viewMode === nextState.viewMode
      ) {
        return;
      }

      model.doAction(
        Actions.updateNodeAttributes(nodeId, {
          config: {
            ...config,
            bookByPriceState: nextState,
          },
        }),
      );
      setDockLayout(model.toJson());
    },
    [setDockLayout],
  );

  // Claim panel focus by activating the panel's FlexLayout tabset. setActiveTabset is
  // the single source of truth: it draws the active-tabset border AND syncs the store +
  // lastFocusedPanelTypeRef through onModelChange → syncFocusFromModel. Panels claim focus
  // this way (never setFocusedPanelType directly) so the visible border and the navbar
  // target can't diverge — FlexLayout's native content-click activation does not fire for
  // side-panel content after Organizar em Grid/Abas rebuilds the model. (#347)
  const claimPanelFocus = useCallback((nodeId) => {
    const model = modelRef.current;
    const node = model?.getNodeById?.(nodeId);
    const tabset = node?.getParent?.();
    if (!model || !tabset) return;
    // #popout: the tabset may live in a popout window's layout — target it.
    const layoutId = tabset.getLayoutId?.();
    // Already the active tabset of its window → still enforce exclusivity
    // (another window may hold a stale active) but skip the redundant action.
    if (model.getActiveTabset?.(layoutId)?.getId?.() === tabset.getId()) {
      enforceSingleActiveTabset(model, layoutId ?? Model.MAIN_LAYOUT_ID);
      return;
    }
    model.doAction(Actions.setActiveTabset(tabset.getId(), layoutId));
  }, []);

  // Return a STABLE claimFocus callback per nodeId (resolves the live node at call time,
  // so it survives Model.fromJson rebuilds) — required because the panels are React.memo.
  const getClaimFocus = useCallback(
    (nodeId) => {
      const cache = claimFocusCacheRef.current;
      let fn = cache.get(nodeId);
      if (!fn) {
        fn = () => claimPanelFocus(nodeId);
        cache.set(nodeId, fn);
      }
      return fn;
    },
    [claimPanelFocus],
  );

  // Factory: render component based on type
  const factory = useCallback(
    (node) => {
      const component = node.getComponent();
      const toolbarProps = getPanelToolbarProps(node);
      const claimFocus = getClaimFocus(node.getId());
      // PoC #popout: every panel carries the bridge so popout documents get the
      // theme mirrored and registered for global listeners (no-op docked).
      const withBridge = (panel) => (
        <>
          <PopoutPanelBridge />
          {panel}
        </>
      );
      if (component === "positions")
        return withBridge(
          <PositionsPanel claimFocus={claimFocus} {...toolbarProps} />,
        );
      if (component === "openOrders")
        return withBridge(
          <OpenOrdersPanel claimFocus={claimFocus} {...toolbarProps} />,
        );
      if (component === "log")
        return withBridge(
          <LogPanel claimFocus={claimFocus} {...toolbarProps} />,
        );
      if (component === "info")
        return withBridge(
          <InfoPanel claimFocus={claimFocus} {...toolbarProps} />,
        );
      if (component === "grid")
        return withBridge(
          <WatchlistPanel claimFocus={claimFocus} {...toolbarProps} />,
        );
      if (component === "bookByPrice") {
        return withBridge(
          <BookByPricePanel
            instanceId={node.getId()}
            initialState={node.getConfig()?.bookByPriceState}
            onBookByPriceStateChange={(partialState) =>
              updateBookByPriceNodeState(node.getId(), partialState)
            }
            claimFocus={claimFocus}
            {...toolbarProps}
          />,
        );
      }
      // Default: chart
      const chartId = node.getConfig()?.chartId;
      if (!chartId) return <div>Empty</div>;
      return withBridge(
        <ChartPanel
          chartId={chartId}
          onInteractiveApiReady={onInteractiveApiReady}
          layoutInvalidationKey={layoutInvalidationKey}
          claimFocus={claimFocus}
        />,
      );
    },
    [
      getClaimFocus,
      getPanelToolbarProps,
      layoutInvalidationKey,
      onInteractiveApiReady,
      updateBookByPriceNodeState,
    ],
  );

  // #popout: closing a flyout DELETES its panels (product decision) — the 0.9
  // lib natively converts the closed window into an in-app float instead
  // (CLOSE_POPOUT sets the layout type to "float"). We post-process the action
  // by deleting every tab of that layout with the same side-effects as the
  // tab's own close button; once the root is empty the lib's tidy removes the
  // layout itself, so the float never shows up.
  // Devolve uma aba de janela destacada para a janela principal (botão que substitui
  // o "destacar" dentro do flyout). Gráficos voltam para o tabset de gráficos; painéis,
  // para a coluna de painéis. Quando a janela destacada fica sem abas, a lib remove o
  // sub-layout e fecha a janela — o closeFlyoutPanels então não acha aba nenhuma para
  // apagar, que é exatamente o que queremos aqui.
  const restoreTabToMainWindow = useCallback((tabNode) => {
    const model = modelRef.current;
    if (!model || !tabNode) return;

    const isChart = (tabNode.getComponent?.() || "chart") === "chart";
    let targetId = null;
    let location = DockLocation.CENTER;
    let index = -1;

    // Hint de origem (gravado ao destacar e salvo no desktop):
    // 1) o tabset de origem ainda existe (ou é reconhecido pelas abas irmãs) -> volta
    //    como aba, na mesma posição;
    // 2) a aba era a ÚNICA do tabset (célula da grade, que sumiu ao destacar) -> recria
    //    a célula no mesmo lado do tabset vizinho.
    const homeHint = tabNode.getConfig?.()?.popoutHome;
    const homeTabset = findHomeTabset(model, homeHint);
    if (homeTabset) {
      targetId = homeTabset.getId();
      const size = homeTabset.getChildren().length;
      const wanted = Number.isInteger(homeHint?.index) ? homeHint.index : -1;
      index = wanted >= 0 && wanted <= size ? wanted : -1;
    } else {
      const homeCell = findHomeCell(model, homeHint);
      if (homeCell) {
        targetId = homeCell.tabsetId;
        location = homeCell.location;
      }
    }

    if (!targetId && isChart) {
      let chartTabset = null;
      const findChartTabset = (node) => {
        if (chartTabset) return;
        if (
          node.getType?.() === "tabset" &&
          node.getChildren().some((c) => c.getComponent?.() === "chart")
        ) {
          chartTabset = node;
          return;
        }
        (node.getChildren?.() || []).forEach(findChartTabset);
      };
      findChartTabset(model.getRootRow()); // só a janela principal
      targetId = chartTabset?.getId?.() ?? null;
    } else if (!targetId) {
      const panelColumn = findPanelColumnNode(model);
      if (panelColumn) {
        const children =
          panelColumn.getType() === "tabset"
            ? [panelColumn]
            : panelColumn.getChildren();
        targetId = children[children.length - 1]?.getId?.() ?? null;
      }
    }

    // Principal sem destino natural (ex.: todos os gráficos destacados): cria a área.
    if (!targetId) {
      targetId = model.getRootRow().getId();
      location = isChart ? DockLocation.RIGHT : DockLocation.LEFT;
    }

    model.doAction(
      Actions.moveNode(tabNode.getId(), targetId, location, index, true),
    );

    // Hint consumido: a aba voltou para a principal.
    if (homeHint) {
      const config = { ...(tabNode.getConfig?.() || {}) };
      delete config.popoutHome;
      model.doAction(Actions.updateNodeAttributes(tabNode.getId(), { config }));
    }
  }, []);

  const closeFlyoutPanels = useCallback(
    (model, layoutId) => {
      if (!layoutId) return;
      const root = model.getRootRow?.(layoutId);
      if (!root) return;
      const tabs = [];
      const walk = (n) => {
        if (n.getType?.() === "tab") tabs.push(n);
        else (n.getChildren?.() || []).forEach(walk);
      };
      walk(root);
      for (const tab of tabs) {
        const component = tab.getComponent?.() || "chart";
        if (
          component === "positions" ||
          component === "openOrders" ||
          component === "log" ||
          component === "grid" ||
          component === "info"
        ) {
          setDockPanelOpen(component, false);
        } else if (component === "bookByPrice") {
          useBookByPriceStore.getState().removeInstance(tab.getId?.() || "");
        } else {
          const chartId = tab.getConfig?.()?.chartId;
          if (chartId) {
            removeChart(chartId);
          }
        }
        model.doAction(Actions.deleteTab(tab.getId()));
      }
    },
    [removeChart, setDockPanelOpen],
  );

  // Persist layout on change + sync focus from FlexLayout model.
  // onModelChange fires after EVERY model mutation (user AND programmatic doAction),
  // making it the universal sync point for focus state.
  const onModelChange = useCallback(
    (model, action) => {
      // #popout: keep exactly one active tabset across ALL windows before
      // syncing focus, so the border and the store agree on a single target.
      const gained = focusGainLayoutId(model, action);
      if (gained != null) enforceSingleActiveTabset(model, gained);
      // Fechar um flyout DELETA seus painéis — mas só quando quem fecha é o usuário.
      // Um rebuild (novo Model) remonta a árvore da flexlayout (o <Layout> bumpa uma
      // key interna quando a identidade do model muda), e as janelas da árvore antiga
      // fecham no unmount, disparando CLOSE_POPOUT a partir do model ESTALE. Esse
      // fechamento não é do usuário: as janelas são recriadas a partir de subLayouts
      // no model novo, e apagar os painéis aqui fazia o gráfico destacado sumir.
      if (action?.type === Actions.CLOSE_POPOUT && model === modelRef.current) {
        closeFlyoutPanels(model, action.data?.layoutId);
      }
      // O ícone de destacar é por janela (na destacada vira "Restaurar aba"), então
      // re-aplica o sweep sempre que uma aba muda de janela. Idempotente: o sweep só
      // dispara updateNodeAttributes quando o valor muda, sem realimentar o loop.
      if (
        action?.type === Actions.POPOUT_TAB ||
        action?.type === Actions.POPOUT_TABSET ||
        action?.type === Actions.MOVE_NODE
      ) {
        applyPopoutFeatureFlag(model);
      }

      // Persiste o hint de origem no config da aba destacada (vai para o JSON do
      // desktop, então o "voltar para o mesmo lugar" sobrevive a salvar/carregar).
      if (action?.type === Actions.POPOUT_TAB && pendingHomeHintRef.current) {
        const { tabId, hint } = pendingHomeHintRef.current;
        pendingHomeHintRef.current = null;
        const node = model.getNodeById?.(tabId);
        if (node && hint) {
          model.doAction(
            Actions.updateNodeAttributes(tabId, {
              config: { ...(node.getConfig?.() || {}), popoutHome: hint },
            }),
          );
        }
      }
      if (!isSyncingRef.current) {
        setDockLayout(model.toJson());
      }
      setTabsetCount(countTabsets(model));
      syncFocusFromModel(model);
      // NOTE: Do NOT dispatch window.resize here. It triggers fitWidth's destructive
      // 0→recalculate cycle on ALL charts, causing visual jumps on every tab click.
      // Panel resizing (splitter drag) is handled by fitWidth's ResizeObserver instead.
    },
    [setDockLayout, closeFlyoutPanels],
  );

  // Handle FlexLayout actions (tab close, tab select)
  const onAction = useCallback(
    (action) => {
      if (isSyncingRef.current) return action;

      // Antes de destacar, guarda de onde a aba saiu (o nó ainda está no lugar antigo).
      // O hint é gravado no config da aba logo depois, no onModelChange.
      if (action.type === Actions.POPOUT_TAB) {
        const node = modelRef.current?.getNodeById?.(action.data?.node);
        pendingHomeHintRef.current = node
          ? {
              tabId: node.getId(),
              hint: captureHomeHint(modelRef.current, node),
            }
          : null;
      }

      if (action.type === Actions.DELETE_TAB) {
        const node = modelRef.current.getNodeById(action.data.node);
        const component = node?.getComponent?.() || "chart";

        if (
          component === "positions" ||
          component === "openOrders" ||
          component === "log" ||
          component === "grid" ||
          component === "info"
        ) {
          setDockPanelOpen(component, false);
          return action; // allow close
        }
        if (component === "bookByPrice") {
          // Multiple instances allowed — just clean up per-instance store data
          useBookByPriceStore.getState().removeInstance(node?.getId?.() || "");
          return action;
        }
        // Chart close logic — fechar o último gráfico é permitido: o app tem
        // outros tipos de painel, e um workspace sem gráfico é um estado válido.
        const chartId = node?.getConfig()?.chartId;
        if (chartId) {
          removeChart(chartId);
        }
      }
      if (action.type === Actions.ADJUST_WEIGHTS) {
        setDockLayoutMode("custom");
      }
      // Focus sync (SELECT_TAB, SET_ACTIVE_TABSET) is handled universally
      // by syncFocusFromModel() in onModelChange — no need to handle here.
      return action;
    },
    [removeChart, setDockLayoutMode, setDockPanelOpen],
  );

  // Sync: detect charts added/removed in store → update FlexLayout model
  useEffect(() => {
    if (!modelRef.current) return;

    const modelChartIds = getChartIdsFromModel(modelRef.current);
    const storeChartIds = new Set(charts.map((c) => c.id));

    // Charts added to store but not in model
    const added = charts.filter((c) => !modelChartIds.has(c.id));
    // Charts in model but removed from store
    const removed = [...modelChartIds].filter((id) => !storeChartIds.has(id));

    if (added.length === 0 && removed.length === 0) return;

    isSyncingRef.current = true;

    // Remove stale tabs
    removed.forEach((chartId) => {
      modelRef.current.visitNodes((node) => {
        if (
          node.getType() === "tab" &&
          node.getComponent() === "chart" &&
          node.getConfig()?.chartId === chartId
        ) {
          modelRef.current.doAction(Actions.deleteTab(node.getId()));
        }
      });
    });

    // Add new charts — preserve existing chart instances by avoiding full model rebuilds.
    // Grid mode inserts charts incrementally into the current chart area instead of
    // recreating the whole FlexLayout model, which causes visible remount/flicker.
    if (added.length > 0) {
      const shouldGrid =
        chartAddMode === "grid" ||
        (chartAddMode === "smart" && charts.length <= 6);
      if (shouldGrid) {
        // Grid mode: add new charts as separate panels without rebuilding the full model.
        added.forEach((chart) => {
          addChartNodeToGrid(modelRef.current, chart, charts.length);
        });
        if (dockLayoutMode === "auto") {
          rebalanceChartGrid(modelRef.current);
        }
      } else {
        // Tabs mode: add to active tabset (if it has charts), otherwise first tabset with charts
        let targetTabsetId = null;
        const activeTabset = modelRef.current.getActiveTabset();
        if (activeTabset) {
          const hasChartTab = activeTabset
            .getChildren()
            .some((c) => c.getComponent?.() === "chart");
          if (hasChartTab) targetTabsetId = activeTabset.getId();
        }
        if (!targetTabsetId) {
          let firstTabsetId = null;
          modelRef.current.visitNodes((node) => {
            if (node.getType() === "tabset") {
              if (!firstTabsetId) firstTabsetId = node.getId();
              if (!targetTabsetId) {
                const hasChartTab = node
                  .getChildren()
                  .some((c) => c.getComponent?.() === "chart");
                if (hasChartTab) targetTabsetId = node.getId();
              }
            }
          });
          // Fallback: if no tabset has chart tabs, use any tabset
          if (!targetTabsetId) targetTabsetId = firstTabsetId;
        }
        added.forEach((chart) => {
          if (targetTabsetId) {
            modelRef.current.doAction(
              Actions.addNode(
                {
                  type: "tab",
                  name: `${chart.symbol || "New"} ${chart.periodicity || ""}`.trim(),
                  component: "chart",
                  config: { chartId: chart.id },
                },
                targetTabsetId,
                DockLocation.CENTER,
                -1,
              ),
            );
          }
        });
      }
    }

    setDockLayout(modelRef.current.toJson());
    setTabsetCount(countTabsets(modelRef.current));
    isSyncingRef.current = false;
  }, [charts, chartAddMode, dockLayoutMode, setDockLayout]);

  // Update tab names when chart symbols/periodicities or BookByPrice symbol change.
  // Also seed instances for inactive book tabs that never mounted (e.g. after a
  // desktop load), so the group-link reaches them and they get named. (#334)
  useEffect(() => {
    seedBookInstancesFromModel(modelRef.current);
    reconcileTabNames(modelRef.current, charts);
    applyPopoutFeatureFlag(modelRef.current);
  }, [charts, bbpDisplaySymbol, bbpInstances]);

  // Register addPanelTab on the store — always opens in dedicated left column, own tabset
  const addPanelTab = useCallback(
    (panelType) => {
      if (!modelRef.current) return;

      const nameMap = {
        positions: "Posições",
        openOrders: "Ordens Abertas",
        log: "Log",
        grid: "Watchlist",
        info: "Info",
        bookByPrice: getBookByPriceTabName(
          bbpDisplaySymbol,
          useBookByPriceStore.getState()._defaults.viewMode,
        ),
      };
      const name = nameMap[panelType] || panelType;
      const bookByPriceDefaults = useBookByPriceStore.getState()._defaults;
      const tabNode = {
        type: "tab",
        name,
        component: panelType,
        config: {
          panelToolbar: { pinned: defaultPanelToolbarPinned },
          ...(panelType === "bookByPrice"
            ? { bookByPriceState: { ...bookByPriceDefaults } }
            : {}),
        },
      };

      const panelColumn = findPanelColumnNode(modelRef.current);

      if (panelType === "info") {
        // Painel Info: tabset dedicado colado na DIREITA da root row, altura cheia
        // (não entra na coluna de painéis da esquerda). Reposicionável como qualquer
        // aba depois. Mesma técnica in-place do branch LEFT (nunca Model.fromJson).
        const rootRow = modelRef.current.getRootRow();
        const newNode = modelRef.current.doAction(
          Actions.addNode(
            tabNode,
            rootRow.getId(),
            DockLocation.RIGHT,
            -1,
            true,
          ),
        );
        const panelTabset = newNode?.getParent?.();
        if (panelTabset) {
          const siblings = rootRow
            .getChildren()
            .filter((child) => child.getId() !== panelTabset.getId());
          modelRef.current.doAction(
            Actions.updateNodeAttributes(panelTabset.getId(), { weight: 20 }),
          );
          siblings.forEach((child) =>
            modelRef.current.doAction(
              Actions.updateNodeAttributes(child.getId(), {
                weight: 80 / siblings.length,
              }),
            ),
          );
          modelRef.current.doAction(Actions.selectTab(newNode.getId()));
        }
      } else if (panelColumn) {
        // Panel column exists — add new tabset below the last child
        const children =
          panelColumn.getType() === "tabset"
            ? [panelColumn]
            : panelColumn.getChildren();
        const lastChild = children[children.length - 1];
        const newNode = modelRef.current.doAction(
          Actions.addNode(tabNode, lastChild.getId(), DockLocation.BOTTOM, -1),
        );
        if (newNode) {
          modelRef.current.doAction(Actions.selectTab(newNode.getId()));
        }
      } else {
        // No panel column yet — create it on the LEFT of the main root row (25%),
        // charts keep the rest (75%). Feito com doAction (mutação in-place), NUNCA
        // com Model.fromJson: trocar a identidade do model faz o <Layout> da
        // flexlayout remontar a árvore inteira, o que fecha (e reabre) as janelas
        // destacadas — o gráfico destacado piscava, e antes até sumia.
        const rootRow = modelRef.current.getRootRow();
        const newNode = modelRef.current.doAction(
          Actions.addNode(
            tabNode,
            rootRow.getId(),
            DockLocation.LEFT,
            -1,
            true,
          ),
        );
        const panelTabset = newNode?.getParent?.();
        if (panelTabset) {
          const siblings = rootRow
            .getChildren()
            .filter((child) => child.getId() !== panelTabset.getId());
          modelRef.current.doAction(
            Actions.updateNodeAttributes(panelTabset.getId(), { weight: 25 }),
          );
          siblings.forEach((child) =>
            modelRef.current.doAction(
              Actions.updateNodeAttributes(child.getId(), {
                weight: 75 / siblings.length,
              }),
            ),
          );
          modelRef.current.doAction(Actions.selectTab(newNode.getId()));
        }
      }

      setDockPanelOpen(panelType, true);
      setDockLayoutMode("auto");
      // O tab novo nasce sem enablePopout/enablePopoutIcon. applyPopoutFeatureFlag
      // só roda no effect de `charts`, então um painel aberto isoladamente (sem
      // mexer em gráfico) ficaria sem o botão de destacar até a próxima mudança
      // de gráfico. Aplica agora para o botão aparecer já na criação.
      applyPopoutFeatureFlag(modelRef.current);
      setDockLayout(modelRef.current.toJson());
      setTabsetCount(countTabsets(modelRef.current));
      // Sync focus for model rebuilds (new Model.fromJson) where onModelChange
      // won't fire until re-render. For existing models, this is a no-op due to ref dedup.
      syncFocusFromModel(modelRef.current);
    },
    [
      bbpDisplaySymbol,
      defaultPanelToolbarPinned,
      setDockLayout,
      setDockLayoutMode,
      setDockPanelOpen,
    ],
  );

  // Register on mount, clear on unmount
  useEffect(() => {
    setAddDockPanelFn(addPanelTab);
    return () => setAddDockPanelFn(null);
  }, [addPanelTab, setAddDockPanelFn]);

  // Live dockLayout getter for desktop snapshots — model.toJson() re-reads
  // each popout window's current screen rect (position/size), which never
  // reaches the store via onModelChange (window moves fire no model action)
  useEffect(() => {
    const store = useWorkspaceStore.getState();
    store.setGetDockLayoutFn(() => modelRef.current?.toJson() ?? null);
    return () => useWorkspaceStore.getState().setGetDockLayoutFn(null);
  }, []);

  // Group selector handlers
  const handleGroupBadgeClick = useCallback((chartId, anchorRef, e) => {
    e.stopPropagation();
    e.preventDefault();
    setGroupSelectorState({ open: true, chartId, anchorRef });
  }, []);

  const handleGroupSelect = useCallback(
    (chartId, group) => {
      if (chartId === "watchlist") {
        useWatchlistStore.getState().setGroup(group);
        return;
      }
      if (chartId?.startsWith("bbp:")) {
        const nodeId = chartId.slice(4);
        useBookByPriceStore.getState().setInstanceGroup(nodeId, group);
        updateBookByPriceNodeState(nodeId, { group });
        return;
      }

      updateChartGroup(chartId, group);
    },
    [updateBookByPriceNodeState, updateChartGroup],
  );

  const handleGroupSelectorClose = useCallback(() => {
    setGroupSelectorState({ open: false, chartId: null, anchorRef: null });
  }, []);

  // One-shot layout reorganization (triggered by navbar buttons)
  const requestOrganize = useWorkspaceStore((state) => state.requestOrganize);
  const setRequestOrganize = useWorkspaceStore(
    (state) => state.setRequestOrganize,
  );
  useEffect(() => {
    if (!requestOrganize || !modelRef.current) return;
    // Remember the focused panel so we can re-activate it after the rebuild.
    // fromJson() drops activeTabSet AND regenerates tabset ids, so key off the panel's
    // preserved identity: chartId for charts, the (preserved) tab id otherwise. (#347)
    const prevSelected = modelRef.current
      .getActiveTabset?.()
      ?.getSelectedNode?.();
    const prevActiveKey = prevSelected
      ? prevSelected.getComponent?.() === "chart"
        ? { chartId: prevSelected.getConfig?.()?.chartId }
        : { tabId: prevSelected.getId?.() }
      : null;
    const currentJson = modelRef.current.toJson();
    const allNonChartTabs = collectNonChartTabs(currentJson.layout);
    // O painel Info é sempre colado à DIREITA (não entra na coluna de painéis da
    // esquerda). Separa-o do resto e trata-o num bloco próprio abaixo.
    const infoTabJson = allNonChartTabs.find((t) => t.component === "info");
    const nonChartTabs = allNonChartTabs.filter((t) => t.component !== "info");
    // One-shot: import de desktop Silverlight pediu para injetar o Info (se não
    // existir). Lê e limpa aqui — vale só para esta reorganização.
    const injectInfo = useWorkspaceStore.getState().pendingInfoInject;
    if (injectInfo) setPendingInfoInject(false);

    // Organizar trata SÓ a janela principal: gráficos destacados ficam nas suas
    // janelas (subLayouts) e não entram na grade/abas — senão apareceriam duas
    // vezes, ou (sem carregar subLayouts adiante) a janela seria fechada e o
    // closeFlyoutPanels apagaria o gráfico.
    const dockedChartIds = collectDockedChartIds(currentJson.layout);
    const dockedCharts = charts.filter((c) => dockedChartIds.has(c.id));

    const makeTab = (chart) => ({
      type: "tab",
      name: `${chart.symbol || "New"} ${chart.periodicity || ""}`.trim(),
      component: "chart",
      config: { chartId: chart.id },
    });

    // Nada na principal para reorganizar (tudo destacado e sem painéis): no-op.
    if (dockedCharts.length === 0 && nonChartTabs.length === 0) {
      setRequestOrganize(null);
      return;
    }

    // Reconstrução IN-PLACE (esvazia e re-adiciona as abas da janela principal), nunca
    // Model.fromJson: trocar a identidade do model faz o <Layout> da flexlayout remontar
    // a árvore inteira, fechando as janelas destacadas — e o fechamento apagava os
    // painéis do flyout (o gráfico destacado sumia).
    const model = modelRef.current;
    isSyncingRef.current = true;

    const mainTabs = [];
    const collectTabs = (node) => {
      if (node.getType?.() === "tab") mainTabs.push(node);
      (node.getChildren?.() || []).forEach(collectTabs);
    };
    collectTabs(model.getRootRow()); // só a principal — subLayouts ficam intocados
    mainTabs.forEach((tab) => model.doAction(Actions.deleteTab(tab.getId())));

    const root = model.getRootRow();

    // Gráficos ancorados: abas num tabset só, ou grade "smart" (mesma geometria do
    // addChartNodeToGrid usado na inserção incremental).
    let chartTabsetId = null;
    let firstChartTabId = null;
    dockedCharts.forEach((chart, i) => {
      if (i === 0) {
        const node = model.doAction(
          Actions.addNode(makeTab(chart), root.getId(), DockLocation.RIGHT, -1),
        );
        chartTabsetId = node?.getParent?.()?.getId() ?? null;
        firstChartTabId = node?.getId?.() ?? null;
      } else if (requestOrganize === "tabs") {
        model.doAction(
          Actions.addNode(
            makeTab(chart),
            chartTabsetId,
            DockLocation.CENTER,
            -1,
          ),
        );
      } else {
        addChartNodeToGrid(model, chart, dockedCharts.length);
      }
    });
    if (requestOrganize !== "tabs" && dockedCharts.length > 1) {
      rebalanceChartGrid(model);
    }

    // Painéis: coluna à esquerda (25%). Em abas, todos num tabset; em grade, um por
    // tabset, empilhados. Os ids das abas são preservados (estado por nó — #334).
    let lastPanelTabsetId = null;
    let firstPanelTabId = null;
    nonChartTabs.forEach((tabJson, i) => {
      const location =
        i === 0
          ? DockLocation.LEFT
          : requestOrganize === "tabs"
            ? DockLocation.CENTER
            : DockLocation.BOTTOM;
      const target = i === 0 ? root.getId() : lastPanelTabsetId;
      const node = model.doAction(
        Actions.addNode(tabJson, target, location, -1),
      );
      if (i === 0) firstPanelTabId = node?.getId?.() ?? null;
      if (i === 0 || requestOrganize !== "tabs") {
        lastPanelTabsetId = node?.getParent?.()?.getId() ?? lastPanelTabsetId;
      }
    });

    // Cada addNode seleciona a aba recém-inserida; o rebuild antigo (fromJson) deixava
    // a PRIMEIRA aba de cada tabset visível. Mantém essa semântica — o foco pós-Organizar
    // é derivado da aba visível (#308).
    if (firstChartTabId) model.doAction(Actions.selectTab(firstChartTabId));
    if (firstPanelTabId) model.doAction(Actions.selectTab(firstPanelTabId));

    // Pesos: painéis 25% / gráficos 75%.
    if (nonChartTabs.length > 0 && dockedCharts.length > 0) {
      const children = root.getChildren();
      const panelChild = children[0];
      const chartChildren = children.slice(1);
      model.doAction(
        Actions.updateNodeAttributes(panelChild.getId(), { weight: 25 }),
      );
      chartChildren.forEach((child) =>
        model.doAction(
          Actions.updateNodeAttributes(child.getId(), {
            weight: 75 / chartChildren.length,
          }),
        ),
      );
    }

    // Painel Info: colado à DIREITA (tabset próprio, weight 20). Docado se já
    // existia antes do rebuild (infoTabJson) OU se um import Silverlight pediu a
    // injeção e ele ainda não existe. Fora do loop da coluna esquerda de propósito.
    const wantInfo =
      infoTabJson || (injectInfo && isFeatureEnabled("infoPanel"));
    if (wantInfo) {
      const infoNode = infoTabJson || {
        type: "tab",
        name: "Info",
        component: "info",
        config: { panelToolbar: { pinned: defaultPanelToolbarPinned } },
      };
      const infoRoot = model.getRootRow();
      const addedInfo = model.doAction(
        Actions.addNode(
          infoNode,
          infoRoot.getId(),
          DockLocation.RIGHT,
          -1,
          true,
        ),
      );
      const infoTabset = addedInfo?.getParent?.();
      if (infoTabset) {
        const sibs = infoRoot
          .getChildren()
          .filter((c) => c.getId() !== infoTabset.getId());
        model.doAction(
          Actions.updateNodeAttributes(infoTabset.getId(), { weight: 20 }),
        );
        sibs.forEach((c) =>
          model.doAction(
            Actions.updateNodeAttributes(c.getId(), {
              weight: 80 / sibs.length,
            }),
          ),
        );
      }
    }

    isSyncingRef.current = false;

    // Seed instances for any book tabs not yet mounted, then reconcile non-chart
    // tab names from the live store: the rebuild copied the persisted node.name
    // (stale — e.g. generic "Livro de Ofertas"), and the reactive rename effect
    // won't fire because its deps don't change on a pure layout toggle. (#334)
    seedBookInstancesFromModel(modelRef.current);
    reconcileTabNames(modelRef.current, charts);

    // Re-activate the panel that was focused before the rebuild so the active border
    // stays continuous through Organizar. Tabset ids changed, so resolve by identity.
    // (Clicking another panel still re-focuses normally — handled by claimFocus.) (#347)
    if (prevActiveKey) {
      let match = null;
      modelRef.current.visitNodes((n) => {
        if (match || n.getType?.() !== "tab") return;
        if (prevActiveKey.chartId != null) {
          if (n.getConfig?.()?.chartId === prevActiveKey.chartId) match = n;
        } else if (n.getId?.() === prevActiveKey.tabId) {
          match = n;
        }
      });
      const restoredTabset = match?.getParent?.();
      if (restoredTabset) {
        modelRef.current.doAction(
          Actions.setActiveTabset(restoredTabset.getId()),
        );
      }
    }

    // Sync panel open state
    const panels = getPanelTypesFromModel(modelRef.current);
    Object.entries(panels).forEach(([type, isOpen]) =>
      setDockPanelOpen(type, isOpen),
    );

    setDockLayoutMode("auto");
    setDockLayout(modelRef.current.toJson());
    setTabsetCount(countTabsets(modelRef.current));
    syncFocusFromModel(modelRef.current);
    setRequestOrganize(null);
  }, [
    requestOrganize,
    charts,
    setDockLayout,
    setDockLayoutMode,
    setDockPanelOpen,
    setRequestOrganize,
    setPendingInfoInject,
    defaultPanelToolbarPinned,
  ]);

  // FlexLayout applies tab content rect changes through an internal rAF redraw.
  // When the objects sidebar toggles, ask Layout to redraw in the same commit cycle
  // so chart content can observe the new tab geometry sooner.
  useLayoutEffect(() => {
    layoutComponentRef.current?.redraw?.();
  }, [sidebarOpen, isRouterOperationsSidebarOpen]);

  return (
    <div
      className={`dock-mode-layout ${isDark ? "flexlayout__theme_dark" : "flexlayout__theme_light"}${tabsetCount <= 1 ? " single-tabset" : ""}`}
      style={{ width: "100%", height: "100%", position: "relative" }}
      onDoubleClickCapture={(e) => {
        // Block double-click maximize on tab strip, but allow it on the maximize toolbar button
        if (e.target.closest(".flexlayout__tab_toolbar_button")) return;
        if (e.target.closest(".flexlayout__tabset_tabbar_outer")) {
          e.stopPropagation();
        }
      }}
    >
      <Layout
        ref={layoutComponentRef}
        model={modelRef.current}
        factory={factory}
        popoutURL="/popout.html"
        popoutClassName={`dock-mode-layout ${
          isDark ? "flexlayout__theme_dark" : "flexlayout__theme_light"
        }`}
        // Kill switch (settings.features.panelPopout): with popout support off
        // the lib uses its native graceful degradation — popout icons hidden
        // and layouts saved with open windows re-dock every tab into the main
        // layout on load (no races, unlike closing windows post-restore).
        supportsPopout={isFeatureEnabled("panelPopout")}
        i18nMapper={flexlayoutI18n}
        onModelChange={onModelChange}
        onAction={onAction}
        onRenderTabSet={(node, renderValues) => {
          renderValues.leading = <TabScrollArrow direction="left" />;
          renderValues.stickyButtons.push(
            <TabScrollArrow key="scroll-right" direction="right" />,
          );

          // Numa janela destacada o botão de destacar não faz sentido (o ícone nativo
          // é escondido em applyPopoutFeatureFlag) — no lugar dele vai "Restaurar aba".
          const selectedTab = node.getSelectedNode?.();
          if (
            node.getType?.() === "tabset" &&
            node.getLayoutId?.() !== Model.MAIN_LAYOUT_ID &&
            selectedTab
          ) {
            renderValues.buttons.push(
              <button
                key="restore-to-main"
                className="flexlayout__tab_toolbar_button"
                title={RESTORE_TAB_TITLE}
                onClick={(e) => {
                  e.stopPropagation();
                  restoreTabToMainWindow(selectedTab);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                data-evo-id="dockmodelayout-btn-restore-tab"
              >
                <RestoreTabIcon />
              </button>,
            );
          }
        }}
        onRenderTab={(node, renderValues) => {
          const component = node.getComponent();

          // Panel-type icon — the first thing in every tab header.
          const iconClass = PANEL_ICONS[component];
          const icon = iconClass ? (
            <i className={`${iconClass} dock-tab-icon`} />
          ) : null;

          // Group badge for single-symbol panels (chart, book, watchlist).
          let badge = null;
          if (component === "chart") {
            const chartId = node.getConfig()?.chartId;
            const chart = charts.find((c) => c.id === chartId);
            if (chart) {
              badge = (
                <TabGroupBadge
                  chartId={chartId}
                  group={chart.group}
                  onBadgeClick={handleGroupBadgeClick}
                />
              );
            }
          } else if (component === "bookByPrice") {
            const nodeId = node.getId();
            badge = (
              <TabGroupBadge
                chartId={`bbp:${nodeId}`}
                group={bbpInstances[nodeId]?.group ?? null}
                onBadgeClick={handleGroupBadgeClick}
              />
            );
          } else if (component === "grid") {
            badge = (
              <TabGroupBadge
                chartId="watchlist"
                group={watchlistGroup}
                onBadgeClick={handleGroupBadgeClick}
              />
            );
          }

          if (icon || badge) {
            renderValues.leading = (
              <span className="dock-tab-leading">
                {icon}
                {badge}
              </span>
            );
          }
        }}
      />
      {groupSelectorState.open && (
        <GroupSelector
          chartId={groupSelectorState.chartId}
          currentGroup={
            groupSelectorState.chartId === "watchlist"
              ? watchlistGroup
              : groupSelectorState.chartId?.startsWith("bbp:")
                ? (bbpInstances[groupSelectorState.chartId.slice(4)]?.group ??
                  null)
                : charts.find((c) => c.id === groupSelectorState.chartId)?.group
          }
          onSelect={handleGroupSelect}
          onClose={handleGroupSelectorClose}
          anchorRef={groupSelectorState.anchorRef}
        />
      )}
    </div>
  );
};

export default React.memo(DockModeLayout);
